import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Tooltip } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlagIcon from '@mui/icons-material/Flag';
import './InstantFeedback.css';

const InstantFeedback = ({ delta, onAnimationComplete }) => {
  const { t } = useTranslation();
  const [showContent, setShowContent] = useState(false);
  const [content, setContent] = useState('');
  const [segmentsState, setSegmentsState] = useState([]);
  const [skillBlocks, setSkillBlocks] = useState([]);
  const animationRef = useRef(null);

  // 11个维度完整列表（key 为后端字段，i18nKey 用于界面展示）
  const allSkills = [
    { key: '工程知识', i18nKey: 'skill_engineering_knowledge' },
    { key: '问题分析', i18nKey: 'skill_problem_analysis' },
    { key: '设计/开发解决方案', i18nKey: 'skill_design_solution' },
    { key: '研究', i18nKey: 'skill_research' },
    { key: '使用现代工具', i18nKey: 'skill_modern_tools' },
    { key: '工程与可持续发展', i18nKey: 'skill_engineering_sustainability' },
    { key: '工程伦理和职业规范', i18nKey: 'skill_professional_ethics' },
    { key: '个人与团队', i18nKey: 'skill_individual_team' },
    { key: '沟通', i18nKey: 'skill_communication' },
    { key: '项目管理', i18nKey: 'skill_project_management' },
    { key: '终身学习', i18nKey: 'skill_lifelong_learning' }
  ];

  // 6个成就名称映射（key 为后端字段，i18nKey 用于界面展示）
  const allAchievements = [
    { key: '质量突破', i18nKey: 'achievement_quality_breakthrough' },
    { key: '难度挑战', i18nKey: 'achievement_difficulty_challenge' },
    { key: '技能专精', i18nKey: 'achievement_skill_mastery' },
    { key: '协作领袖', i18nKey: 'achievement_collaboration_leader' },
    { key: '创新先锋', i18nKey: 'achievement_innovation_pioneer' },
    { key: '全面发展', i18nKey: 'achievement_well_rounded' }
  ];

  // 根据中文名称获取翻译后的名称
  const getLocalizedSkillName = (chineseName) => {
    const skill = allSkills.find(s => s.key === chineseName);
    return skill ? t(skill.i18nKey) : chineseName;
  };

  // 根据中文成就名称获取翻译后的名称
  const getLocalizedAchievementName = (chineseName) => {
    const ach = allAchievements.find(a => a.key === chineseName);
    return ach ? t(ach.i18nKey) : chineseName;
  };

  useEffect(() => {
    // 清除之前的定时器
    if (animationRef.current) {
      clearTimeout(animationRef.current);
      animationRef.current = null;
    }

    if (!delta) {
      // delta被清空时，重置所有显示状态（为下一次新delta做准备）
      setShowContent(false);
      setContent('');
      setSkillBlocks([]);
      setSegmentsState([]);
      return;
    }

    //console.log('🎮 [InstantFeedback] 开始执行动画逻辑');
    
    // 构建11维能力方块数据
    const blocks = allSkills.map(skill => {
      const change = delta.skillChanges?.[skill.key];
      const xpGained = change?.xpGained || 0;
      
      return {
        key: skill.key,
        i18nKey: skill.i18nKey,
        xpGained,
        leveledUp: change?.leveledUp || false,
        hasChange: xpGained > 0
      };
    });
    
    // 找出本次增长的最大经验值（至少为1避免除0）
    const maxXpGained = Math.max(...blocks.map(b => b.xpGained), 1);
    
    // 根据相对比例设定颜色
    blocks.forEach(block => {
      let color = 'rgba(100, 100, 100, 0.3)'; // 默认灰色（无变化）
      
      if (block.xpGained > 0) {
        const ratio = block.xpGained / maxXpGained; // 计算占比
        if (ratio >= 0.7) {
          color = '#FF7043'; // 橙红（主要增长，占比70%+）- 提高亮度
        } else if (ratio >= 0.4) {
          color = '#FFD54F'; // 黄色（次要增长，占比40-70%）- 提高亮度
        } else {
          color = '#66BB6A'; // 绿色（少量增长，占比<40%）- 提高亮度
        }
      }
      
      // 升级优先级最高
      if (block.leveledUp) {
        color = '#AB47BC'; // 紫色（技能升级）- 提高亮度
      }
      
      block.color = color;
    });
    
    setSkillBlocks(blocks);
    
    // 构建显示内容（结构化，便于分隔与样式化）
    const segments = [];
    
    // 1. 总经验值提升
    if (delta.xpGained > 0) {
      segments.push({ type: 'xp', text: `+${Math.round(delta.xpGained)} XP`, value: Math.round(delta.xpGained) });
    }
    
    // 2. 新成就：成就名称+成就数据指标值
    if (delta.newAchievements && delta.newAchievements.length > 0) {
      delta.newAchievements.forEach(achievement => {
        const rawName = achievement.name || achievement.triggeredAchievements || '';
        const achievementName = rawName ? getLocalizedAchievementName(rawName) : t('instant_feedback_new_achievement');
        const quality = achievement.quality || achievement.qualityScore || 8;
        segments.push({ 
          type: 'achievement', 
          text: achievementName,
          quality: quality
        });
      });
    }
    
    // 3. 闯关：关卡名称（行为类型）+ 熟练度等级+总完成次数
    if (delta.completedLevels && delta.completedLevels.length > 0) {
      delta.completedLevels.forEach(level => {
        segments.push({ 
          type: 'level', 
          text: getLocalizedSkillName(level.behaviorType),
          masteryTier: level.masteryTier,
          completionCount: level.completionCount
        });
      });
    }

    const displayContent = segments.map(s => s.text).join(' | ');
    //console.log('🎮 [InstantFeedback] 显示内容:', displayContent);
    setContent(displayContent);
    setSegmentsState(segments);
    setShowContent(true);

    // 设置60秒后自动清除（如果中途没有新delta覆盖）
    animationRef.current = setTimeout(() => {
      setShowContent(false);
      setContent('');
      setSkillBlocks([]);
      setSegmentsState([]);
      onAnimationComplete?.();
    }, 60000);

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delta, t]);

  // 辅助函数：获取星级评价
  const getStarRating = (quality) => {
    if (quality >= 8) return '⭐⭐⭐';
    if (quality >= 6) return '⭐⭐';
    return '⭐';
  };

  // 辅助函数：获取熟练度图标（兼容后端中文字段）
  const getMasteryIcon = (tier) => {
    if (tier === '精通') return '⚡⚡⚡⚡⚡';
    if (tier === '熟练') return '⚡⚡⚡';
    return '⚡';
  };

  // 辅助函数：获取熟练度本地化名称
  const getLocalizedMasteryTier = (tier) => {
    if (tier === '精通') return t('instant_feedback_mastery_proficient');
    if (tier === '熟练') return t('instant_feedback_mastery_familiar');
    return tier;
  };

  if (!delta || !showContent) return null;

  return (
    <Box className="instant-feedback-container-v2">
      {/* 11维能力热力图 - 固定不动 */}
      <Box className="skill-heatmap-fixed">
        {skillBlocks.map((block, index) => (
          <Tooltip 
            key={index} 
            title={`${t(block.i18nKey)}: ${block.xpGained > 0 ? t('instant_feedback_xp', { xp: block.xpGained }) : t('instant_feedback_no_change')}`}
            arrow
            placement="top"
          >
            <Box
              className={`skill-block ${block.hasChange ? 'skill-block-active' : ''} ${block.leveledUp ? 'skill-block-levelup' : ''}`}
              sx={{
                width: 14,
                height: 14,
                backgroundColor: block.color,
                borderRadius: '3px',
                marginRight: index < 10 ? '2px' : 0,
                cursor: 'pointer'
              }}
            />
          </Tooltip>
        ))}
      </Box>
      
      {/* 分隔符 */}
      {segmentsState.length > 0 && <Box className="separator-fixed">|</Box>}
      
      {/* 滚动内容区域 */}
      <Box className="feedback-scroll-content">
        <Box className={`feedback-scroll-inner ${segmentsState.length > 2 ? 'scroll-active' : ''}`}>
          {/* 内容区域 */}
          {segmentsState.map((seg, index) => {
            const isXP = seg.type === 'xp';
            const isAch = seg.type === 'achievement';
            const isLevel = seg.type === 'level';

            return (
              <React.Fragment key={index}>
                {index > 0 && <Box className="separator">|</Box>}
                
                {/* 总经验值 */}
                {isXP && (
                  <Box className="xp-section">
                    <TrendingUpIcon sx={{ fontSize: 18, color: '#FFD700' }} />
                    <span style={{ color: '#FFD700', fontWeight: 700, fontSize: '14px' }}>
                      {seg.text}
                    </span>
                  </Box>
                )}
                
                {/* 成就 */}
                {isAch && (
                  <Box className="achievement-section">
                    <EmojiEventsIcon sx={{ fontSize: 18, color: '#FF6B35' }} />
                    <span style={{ color: '#FF6B35', fontWeight: 700, fontSize: '14px' }}>
                      {seg.text}
                    </span>
                    <span style={{ color: '#FFC107', fontSize: '12px', marginLeft: '4px' }}>
                      {getStarRating(seg.quality)}
                    </span>
                  </Box>
                )}
                
                {/* 闯关 */}
                {isLevel && (
                  <Box className="level-section">
                    <FlagIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
                    <span style={{ color: '#4CAF50', fontWeight: 700, fontSize: '14px' }}>
                      {seg.text}
                    </span>
                    <span style={{ color: '#FFD700', fontSize: '12px', marginLeft: '4px' }}>
                      {getMasteryIcon(seg.masteryTier)}
                    </span>
                    <span style={{ color: '#66BB6A', fontSize: '12px', marginLeft: '4px' }}>
                      {t('instant_feedback_times', { count: seg.completionCount })}
                    </span>
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default InstantFeedback;